import React, { useState, useMemo, useCallback } from "react";
import moment from "moment-timezone";
import Pagination from "../Pagination";
import withNavigate from "../withNavigate";
import { periodButtons, recordsData } from "../../../utils/constants";
import ChartWrapper from "../ChartWrapper";
import SelectInput from "../SelectInput";
import { DownArrowIcon, UpArrowIcon } from "../SvgIcon";
import comingSoonImg from "../../../icons/coming-soon.avif";

const RptMembershipCancelled = () => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [selectedPeriod, setSelectedPeriod] = useState("12M");
  const [selectedInterval, setSelectedInterval] = useState("Month");
  const [selectedChartType, setSelectedChartType] = useState("totals");
  const [chartViewMode, setChartViewMode] = useState("bar");
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [sort, setSort] = useState("");
  const [recordTypeFilter, setRecordTypeFilter] = useState("any");

  // Static data for metrics
  const metricsData = {
    totalDeletions: { value: 165, change: 148, percentage: 871 },
    clientDeletions: { value: 97, change: -86, percentage: 82 },
    driverDeletions: { value: 68, change: 21, percentage: 100 },
  };

  const handleInputChange = useCallback(
    (event) => {
      const { name, value, type, checked } = event.target;
      const inputValue = type === "checkbox" ? checked : value;

      switch (name) {
        case "search":
          setSearchValue(inputValue);
          break;
        case "record":
          setRecordTypeFilter(inputValue);
          setPage(1);
          break;
        default:
          break;
      }
    },
    [setSearchValue, setRecordTypeFilter, setPage],
  );

  const generateChartData = (interval) => {
    const data = {};
    const reasons = [
      {
        key: "noLongerNeed",
        label: "I no longer need the service",
        color: "#ec4899",
      },
      {
        key: "notBooking",
        label: "I am not booking enough jobs",
        color: "#22c55e",
      },
      {
        key: "qualityNotExpected",
        label: "Quality of jobs is not as expected",
        color: "#ec4899",
      },
      { key: "tooExpensive", label: "It's too expensive", color: "#8b5cf6" },
      { key: "notUsing", label: "Not using it enough", color: "#6366f1" },
      {
        key: "difficultyApp",
        label: "Difficulty in using the app",
        color: "#06b6d4",
      },
      { key: "other", label: "Other", color: "#94a3b8" },
    ];

    const userTypes = [
      { key: "monthly", label: "Monthly", color: "#84cc16" },
      { key: "yearly", label: "Yearly", color: "#f59e0b" },
    ];

    const jobTypes = [
      { key: "houseMove", label: "House Move", color: "#ec4899" },
      { key: "sendItems", label: "Send Items", color: "#06b6d4" },
      { key: "wasteDisposal", label: "Waste Disposal", color: "#84cc16" },
    ];

    const generateDataPoint = (total) => ({
      total: total,
      monthly: Math.floor(total * 0.6),
      yearly: Math.floor(total * 0.4),
      noLongerNeed: Math.floor(total * 0.12),
      notBooking: Math.floor(total * 0.14),
      qualityNotExpected: Math.floor(total * 0.12),
      tooExpensive: Math.floor(total * 0.16),
      notUsing: Math.floor(total * 0.18),
      difficultyApp: Math.floor(total * 0.2),
      other: Math.ceil(total * 0.08),
      houseMove: Math.floor(total * 0.5),
      sendItems: Math.floor(total * 0.3),
      wasteDisposal: Math.floor(total * 0.2),
    });

    // 7 Days - varies by interval
    data["7D"] = [];
    if (interval === "Day") {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "days");
        const totals = [3, 5, 2, 4, 6, 3, 2];
        const total = totals[6 - i];
        data["7D"].push({
          month: date.format("ddd D MMM"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "days");
        const totals = [3, 5, 2, 4, 6, 3, 2];
        const total = totals[6 - i];
        data["7D"].push({
          month: date.format("D MMM"),
          ...generateDataPoint(total),
        });
      }
    }

    // 30 Days
    data["30D"] = [];
    if (interval === "Day") {
      for (let i = 29; i >= 0; i--) {
        const date = moment().subtract(i, "days");
        const total = Math.floor(Math.random() * 5) + 2;
        data["30D"].push({
          month: date.format("D MMM"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i * 4, "days");
        const totals = [8, 12, 15, 18, 14, 16, 19];
        const total = totals[6 - i];
        data["30D"].push({
          month: date.format("D MMM"),
          ...generateDataPoint(total),
        });
      }
    }

    // 3 Months
    data["3M"] = [];
    if (interval === "Month") {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "months");
        const totals = [9, 12, 11, 26, 19, 7, 15];
        const total = totals[6 - i];
        data["3M"].push({
          month: date.format("MMM YYYY"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const date = moment().subtract(i, "months");
        const totals = [9, 12, 11, 26, 19, 7, 15];
        const total = totals[6 - i];
        data["3M"].push({
          month: date.format("MMM YYYY"),
          ...generateDataPoint(total),
        });
      }
    }

    // 6 Months
    data["6M"] = [];
    for (let i = 6; i >= 0; i--) {
      const date = moment().subtract(i, "months");
      const totals = [9, 10, 22, 9, 12, 11, 26];
      const total = totals[6 - i];
      data["6M"].push({
        month: date.format("MMM YYYY"),
        ...generateDataPoint(total),
      });
    }

    // 12 Months
    data["12M"] = [];
    if (interval === "Year") {
      for (let i = 2; i >= 0; i--) {
        const date = moment().subtract(i, "years");
        const total = Math.floor(Math.random() * 40) + 80;
        data["12M"].push({
          month: date.format("YYYY"),
          ...generateDataPoint(total),
        });
      }
    } else {
      for (let i = 11; i >= 0; i--) {
        const date = moment().subtract(i, "months");
        const totals = [18, 10, 12, 9, 10, 22, 9, 12, 11, 26, 19, 3];
        const total = totals[11 - i];
        data["12M"].push({
          month: date.format("MMM YYYY"),
          ...generateDataPoint(total),
        });
      }
    }

    return { data, reasons, userTypes, jobTypes };
  };

  const {
    data: chartData,
    reasons,
    userTypes,
    jobTypes,
  } = useMemo(() => generateChartData(selectedInterval), [selectedInterval]);

  const intervalOptions = [
    { value: "Day", label: "Day" },
    { value: "Week", label: "Week" },
    { value: "Month", label: "Month" },
  ];

  const chartTypeOptions = [
    { value: "totals", label: "Totals" },
    { value: "plan", label: "By Plan" },
    { value: "drivers", label: "By Reason" },
  ];

  const getCurrentChartData = () => {
    return chartData[selectedPeriod] || chartData["12M"];
  };

  const renderAnalyticsTab = () => {
    return (
      <div className="booking-analytics">
        <div className="analytics-controls">
          <div className="period-buttons">
            {periodButtons?.map((button) => (
              <button
                key={button.id}
                className={`period-btn ${
                  selectedPeriod === button.id ? "active" : ""
                }`}
                onClick={() => setSelectedPeriod(button.id)}
              >
                {button.label}
              </button>
            ))}
          </div>

          <div className="interval-selector">
            <span className="interval-label">Interval:</span>
            <SelectInput
              value={intervalOptions.find(
                (opt) => opt.value === selectedInterval,
              )}
              onChange={(option) => setSelectedInterval(option.value)}
              options={intervalOptions}
              isSearchable={false}
              className="interval-select"
            />
          </div>
        </div>

        <div className="metrics-cards">
          <div className="metric-card">
            <div className="metric-title">Total Cancellations</div>
            <div className="d-flex align-items-center">
              <span className="metric-value me-5">
                {metricsData?.totalDeletions?.value}
              </span>
              <span
                className={`metric-change ${metricsData?.totalDeletions?.change >= 0 ? "positive" : "negative"}`}
              >
                {metricsData?.totalDeletions?.change >= 0 ? (
                  <UpArrowIcon />
                ) : (
                  <DownArrowIcon />
                )}
                {metricsData?.totalDeletions?.change >= 0 ? "+" : "-"}
                {Math.abs(metricsData?.totalDeletions?.change)} (
                {metricsData?.totalDeletions?.percentage}%)
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Monthly Plan</div>
            <div className="d-flex align-items-center">
              <span className="metric-value me-5">
                {metricsData?.clientDeletions?.value}
              </span>
              <span
                className={`metric-change ${metricsData?.clientDeletions?.change >= 0 ? "positive" : "negative"}`}
              >
                {metricsData?.clientDeletions?.change >= 0 ? (
                  <UpArrowIcon />
                ) : (
                  <DownArrowIcon />
                )}
                {metricsData?.clientDeletions?.change >= 0 ? "+" : "-"}
                {Math.abs(metricsData?.clientDeletions?.change)} (
                {metricsData?.clientDeletions?.percentage}%)
              </span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Yearly Plan</div>
            <div className="d-flex align-items-center">
              <span className="metric-value me-5">
                {metricsData?.driverDeletions?.value}
              </span>
              <span
                className={`metric-change ${metricsData?.driverDeletions?.change >= 0 ? "positive" : "negative"}`}
              >
                {metricsData?.driverDeletions?.change >= 0 ? (
                  <UpArrowIcon />
                ) : (
                  <DownArrowIcon />
                )}
                {metricsData?.driverDeletions?.change >= 0 ? "+" : "-"}
                {Math.abs(metricsData?.driverDeletions?.change)} (
                {metricsData?.driverDeletions?.percentage}%)
              </span>
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div className="chart-section">
          <ChartWrapper
            data={getCurrentChartData()}
            chartTypeOptions={chartTypeOptions}
            selectedChartType={selectedChartType}
            onChartTypeChange={setSelectedChartType}
            chartViewMode={chartViewMode}
            onChartViewModeChange={setChartViewMode}
            dataKey="month"
            height={350}
            series={
              selectedChartType === "drivers"
                ? reasons
                : selectedChartType === "plan"
                  ? userTypes
                  : selectedChartType === "jobTypes"
                    ? jobTypes
                    : null
            }
            valueKey="total"
          />
        </div>
      </div>
    );
  };

  const sortRecords = (event, field) => {
    if (event) event.preventDefault();

    const sortFields = {
      firstname: { asc: "firstname=1", desc: "firstname=-1" },
      reason: { asc: "reason=1", desc: "reason=-1" },
      plan: { asc: "plan=1", desc: "plan=-1" },
      age: { asc: "age=1", desc: "age=-1" },
      access: { asc: "access=1", desc: "access=-1" },
      earnings: { asc: "earnings=1", desc: "earnings=-1" },
      bookings: { asc: "bookings=1", desc: "bookings=-1" },
      quotes: { asc: "quotes=1", desc: "quotes=-1" },
      statusSorter: { asc: "statusSorter=1", desc: "statusSorter=-1" },
    };

    const currentSort = sortFields[field];
    if (!currentSort) return;

    const newSort =
      sort === currentSort.asc ? currentSort.desc : currentSort.asc;

    setSearch("");
    setSort(newSort);
    setPage(1);
  };
  const getSortClass = (key) => {
    if (sort === `${key}=1`) {
      return "c-list-filter__button c-list-filter__button--icon-up";
    } else if (sort === `${key}=-1`) {
      return "c-list-filter__button c-list-filter__button--icon-down";
    }
    return "c-list-filter__button c-list-filter__button--faded";
  };

  const SortButton = useCallback(
    ({ field, label }) => (
      <button
        className={getSortClass(field)}
        onClick={(e) => sortRecords(e, field)}
      >
        {label}
      </button>
    ),
    [getSortClass, sortRecords],
  );

  const renderRecordData = (record, index) => {
    return (
      <div className="c-list-entry align-items-center" key={index}>
        <div className="c-list-entry__name">
          <div className="text-capitalize">
            {record?.cancelledBy?.name || "-"}
            <br />
            <span className="text-sm text-gray-600 ">
              {record?.cancelledBy?.code}
            </span>
          </div>
        </div>
        <div className="c-list-entry__rating">
          {moment(record?.cancelledOn?.date)
            ?.tz("Europe/London")
            ?.format("DD MMM YYYY")}

          <br />
          <span className="text-sm text-gray-600 ">
            {moment(record?.cancelledOn?.date)
              ?.tz("Europe/London")
              ?.format("hh:mm ")}
          </span>
        </div>
        <div className="c-list-entry__reason">{record?.reason || "-"}</div>
        <div className="c-list-entry__rating">{"Yearly"}</div>
        <div className="c-list-entry__rating">{"7 months"}</div>
        <div className="c-list-entry__rating">{"10 Jul 2026"}</div>
        <div className="c-list-entry__mavquotes">{"£1,254.00"}</div>
        <div className="c-list-entry__mavquotes">{"6"}</div>
        <div className="c-list-entry__mavquotes">{"37"}</div>
        <div className="c-list-entry__mavquotes">
          <button type="button" className="c-btn c-btn--teal c-btn--small me-0">
            View
          </button>
        </div>
      </div>
    );
  };

  const ColumnHeaders = React.memo(({ sort, onSort }) => (
    <div>
      <div className="c-list-entry c-list-entry__header">
        <div className="c-list-entry__name c-list-filter">
          <SortButton field="firstname" label="MAV" />
        </div>
        <div className="c-list-entry__rating c-list-filter">
          <SortButton field="statusSorter" label="Cancelled On" />
        </div>
        <div className="c-list-entry__reason c-list-filter">
          <SortButton field="reason" label="Reason" />
        </div>
        <div className="c-list-entry__rating c-list-filter">
          <SortButton field="plan" label="Plan" />
        </div>
        <div className="c-list-entry__rating c-list-filter">
          <SortButton field="age" label="Membership age" />
        </div>
        <div className="c-list-entry__rating c-list-filter">
          <SortButton field="access" label="Access Until" />
        </div>
        <div className="c-list-entry__mavquotes c-list-filter">
          <SortButton field="earnings" label="Earnings" />
        </div>
        <div className="c-list-entry__mavquotes c-list-filter">
          <SortButton field="bookings" label="Bookings" />
        </div>
        <div className="c-list-entry__mavquotes c-list-filter">
          <SortButton field="quotes" label="Quotes" />
        </div>
        <div className="c-list-entry__mavquotes c-list-filter">Actions</div>
      </div>
    </div>
  ));

  const searchRecords = () => {
    setSearch(searchValue);
    setRecordTypeFilter("all");
    setPage(1);
  };
  const renderRecordsTab = () => {
    return (
      <div>
        <div className="">
          <div className="c-subheader-info-panel">
            <div className="c-subheader-info-panel__search">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  searchRecords();
                }}
              >
                <input
                  type="text"
                  value={searchValue || ""}
                  onChange={handleInputChange}
                  name="search"
                  placeholder="Search records..."
                  className="c-subheader-info-panel__input"
                />
                <button
                  type="button"
                  className="c-btn-outline c-btn-outline--white c-btn-outline--small"
                  onClick={() => searchValue !== null && searchRecords()}
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          <div className="c-hr"></div>

          <div className="l-row">
            <div className="l-col-100 u-padding-top-0 u-padding-bottom-0">
              <div className="filter-bar">
                {[
                  {
                    id: "any",
                    label: "Any",
                  },
                  {
                    id: "monthly",
                    label: "Monthly",
                  },
                  {
                    id: "yearly",
                    label: "Yearly",
                  },
                ]?.map(({ id, label }) => (
                  <div className="c-checkbox-radio-block" key={id}>
                    <input
                      type="radio"
                      id={id}
                      name="record"
                      value={id}
                      onChange={handleInputChange}
                      checked={recordTypeFilter === id}
                    />
                    <label htmlFor={id}>{label}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="l-col-100">
              <div className="c-hr u-margin-bottom-0"></div>

              <ColumnHeaders />
              <div>
                {recordsData?.length > 0 ? (
                  recordsData?.map(renderRecordData)
                ) : (
                  <div className="u-text-align-center u-margin-top-bottom-m u-color-red">
                    No records found
                  </div>
                )}
              </div>

              <div className="c-hr u-margin-bottom-0 u-clear-both"></div>

              <Pagination
                pageSize={pageSize}
                totalPages={totalPages}
                page={page}
                setPage={setPage}
                inputValue={inputValue}
                setInputValue={setInputValue}
                setPageSize={setPageSize}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="l-base-layout__content">
      <h1>Membership Cancellations</h1>
      <div className="c-hr"></div>
      {/* <div className="custom-tabs">
        <div
          className={`tab-item ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytics
        </div>
        <div
          className={`tab-item ${activeTab === "records" ? "active" : ""}`}
          onClick={() => setActiveTab("records")}
        >
          Records
        </div>
      </div> */}

      <div>
        {/* {activeTab === "analytics" ? renderAnalyticsTab() : renderRecordsTab()} */}
        <div className="coming-soon-img-section">
          <img src={comingSoonImg} alt="Coming-Soon" width={550} />
        </div>
      </div>
    </div>
  );
};

export default withNavigate(RptMembershipCancelled);
